import { WillData } from './schema';

type DeepPartial<T> =
    T extends (infer U)[]
    ? DeepPartial<U>[]
    : T extends object
    ? { [K in keyof T]?: DeepPartial<T[K]> }
    : T;

// Helper to update a person in a list
const updateList = (list: any[], oldFullName: string, newPerson: any) => {
    if (!list) return list;
    return list.map(item => {
        if (item.fullName === oldFullName) {
            return {
                ...item,
                ...newPerson,
                // Preserve existing fields if not provided in newPerson
                relationship: newPerson.relationship || item.relationship,
                address: newPerson.address || item.address
            };
        }
        return item;
    });
};

// Helper to update gift recipients and substitute recipients
const updateGiftRecipients = (gifts: any[], oldFullName: string, newPerson: any) => {
    if (!gifts) return gifts;
    return gifts.map(gift => ({
        ...gift,
        // Update main recipient if it matches
        recipient: gift.recipient?.fullName === oldFullName
            ? { ...gift.recipient, fullName: newPerson.fullName, address: newPerson.address || gift.recipient.address }
            : gift.recipient,
        // Update substitute recipient if it matches
        substituteRecipient: gift.substituteRecipient?.fullName === oldFullName
            ? { ...gift.substituteRecipient, fullName: newPerson.fullName, address: newPerson.address || gift.substituteRecipient.address }
            : gift.substituteRecipient
    }));
};

export const updatePersonInWill = (
    data: DeepPartial<WillData>,
    oldFullName: string,
    newPerson: { fullName: string; relationship?: string; address?: any; email?: string; isOver18?: boolean }
) => {
    const newData = { ...data };

    // Update Personal Details (Spouse/Partner)
    if (newData.personalDetails?.spouseFullName === oldFullName) {
        newData.personalDetails.spouseFullName = newPerson.fullName;
        if (newPerson.address) {
            // If they live apart, update the spouse address
            newData.personalDetails.spouseAddress = newPerson.address;
        }
    }

    // Update Intended Partner
    if ((newData.personalDetails as any)?.intendedPartnerFullName === oldFullName) {
        (newData.personalDetails as any).intendedPartnerFullName = newPerson.fullName;
        if (newPerson.address) {
            (newData.personalDetails as any).intendedPartnerAddress = newPerson.address;
        }
    }

    // Update Arrays
    newData.children = updateList(newData.children as any[], oldFullName, newPerson);
    newData.guardians = updateList(newData.guardians as any[], oldFullName, newPerson);
    newData.petGuardians = updateList(newData.petGuardians as any[], oldFullName, newPerson);
    newData.executors = updateList(newData.executors as any[], oldFullName, newPerson);
    newData.substituteExecutors = updateList(newData.substituteExecutors as any[], oldFullName, newPerson);
    newData.beneficiaries = updateList(newData.beneficiaries as any[], oldFullName, newPerson);

    // Update gifts (recipient and substitute recipient)
    newData.gifts = updateGiftRecipients(newData.gifts as any[], oldFullName, newPerson);

    // Update manuallyAddedPeople
    if (newData.manuallyAddedPeople) {
        newData.manuallyAddedPeople = updateList(newData.manuallyAddedPeople as any[], oldFullName, newPerson);
    }

    return newData;
};

// Helper to filter a person from a list
const filterList = (list: any[], fullName: string) => {
    if (!list) return list;
    return list.filter(item => item.fullName !== fullName && item.companyName !== fullName);
};

// Helper to filter substitutes from beneficiaries
const filterBeneficiarySubstitutes = (beneficiaries: any[], fullName: string) => {
    if (!beneficiaries) return beneficiaries;
    return beneficiaries.map(beneficiary => ({
        ...beneficiary,
        substitutes: beneficiary.substitutes
            ? beneficiary.substitutes.filter((sub: any) => sub.fullName !== fullName)
            : beneficiary.substitutes
    }));
};

// Helper to filter gift recipients and substitute recipients
const filterGiftRecipients = (gifts: any[], fullName: string) => {
    if (!gifts) return gifts;
    return gifts
        .filter(gift => {
            // Remove gift if main recipient matches
            if (gift.recipient?.fullName === fullName) return false;
            return true;
        })
        .map(gift => ({
            ...gift,
            // Clear substitute recipient if it matches
            substituteRecipient: gift.substituteRecipient?.fullName === fullName
                ? undefined
                : gift.substituteRecipient
        }));
};

export const removePersonFromWill = (
    data: DeepPartial<WillData>,
    fullName: string
) => {
    const newData = { ...data };

    // Remove from Personal Details (Spouse/Partner)
    if (newData.personalDetails?.spouseFullName === fullName) {
        newData.personalDetails.spouseFullName = undefined;
        newData.personalDetails.spouseAddress = undefined;
        newData.personalDetails.spouseLivesWithYou = undefined;
    }

    // Remove from Intended Partner
    if ((newData.personalDetails as any)?.intendedPartnerFullName === fullName) {
        (newData.personalDetails as any).intendedPartnerFullName = undefined;
        (newData.personalDetails as any).intendedPartnerAddress = undefined;
        (newData.personalDetails as any).intendedPartnerLivesWithYou = undefined;
    }

    // Remove from all arrays
    newData.children = filterList(newData.children as any[], fullName);
    newData.guardians = filterList(newData.guardians as any[], fullName);
    newData.petGuardians = filterList(newData.petGuardians as any[], fullName);
    newData.executors = filterList(newData.executors as any[], fullName);
    newData.substituteExecutors = filterList(newData.substituteExecutors as any[], fullName);

    // Remove from beneficiaries (including their substitutes)
    newData.beneficiaries = filterList(newData.beneficiaries as any[], fullName);
    newData.beneficiaries = filterBeneficiarySubstitutes(newData.beneficiaries as any[], fullName);

    // Remove from gifts (as recipient or substitute recipient)
    newData.gifts = filterGiftRecipients(newData.gifts as any[], fullName);

    // Remove from manually added people
    if (newData.manuallyAddedPeople) {
        newData.manuallyAddedPeople = filterList(newData.manuallyAddedPeople as any[], fullName);
    }

    return newData;
};

export const isEstateSectionComplete = (data: DeepPartial<WillData>): boolean => {
    // 1. Must have at least one beneficiary
    if (!data.beneficiaries || data.beneficiaries.length === 0) {
        return false;
    }

    // 2. Percentages must total 100% (allow small float error)
    const totalPercentage = data.beneficiaries.reduce((sum, b) => sum + (Number(b?.percentage) || 0), 0);
    if (Math.abs(totalPercentage - 100) > 0.1) {
        return false;
    }

    // 3. All beneficiaries must have a predecease policy
    const allPoliciesSet = data.beneficiaries.every(b => {
        if (!b?.predeceasePolicy) return false;

        // If policy is 'substitute', substitutes must total 100%
        if (b.predeceasePolicy === 'substitute') {
            const subTotal = b.substitutes?.reduce((sum, s) => sum + (Number(s?.percentage) || 0), 0) || 0;
            return Math.abs(subTotal - 100) < 0.1;
        }

        return true;
    });

    if (!allPoliciesSet) {
        return false;
    }

    return true;
};

export const checkAllSectionsComplete = (data: DeepPartial<WillData>): boolean => {
  // About You - complete if personal details filled AND survivorship acknowledged
  const aboutYouComplete = !!data.personalDetails?.fullName && data.personalDetails?.understandsSurvivorship === true;

    // Guardians - complete if children AND pets questions answered
    const hasChildrenAnswered = data.hasChildren === true || data.hasChildren === false;
    const hasPetsAnswered = data.hasPets === true || data.hasPets === false;
    const childrenValid = data.hasChildren === false || (data.children?.length ?? 0) > 0;
    const petsValid = data.hasPets === false || (data.pets?.length ?? 0) > 0;
    const guardiansComplete = hasChildrenAnswered && hasPetsAnswered && childrenValid && petsValid;

    // Your Estate - use data validation instead of flag
    const estateComplete = isEstateSectionComplete(data);

    // Executors - complete if executors added
    const executorsComplete = (data.executors?.length ?? 0) > 0;

    return aboutYouComplete && guardiansComplete && estateComplete && executorsComplete;
};
